import * as React from "react";
import { Search } from "lucide-react";
import { Dialog, Input, InputGroup, PaginationSimple } from "@/components/ui";
import { ContextType, DesignPackageSummary } from "@/services/bedrock/dtos/DesignPackage";
import { useDebounced } from "@/utils/useDebounced";
import { useDesignPackages } from "../hooks/useDesignPackages";
import { GLOBALS_STALE_TIME } from "./InspirationStrip";
import { PackageCard } from "./PackageCard";
import { TypeFilter } from "./PackageListToolbar";

export const GALLERY_PAGE_SIZE = 48;
export const SEARCH_DEBOUNCE_MS = 400;

export interface TemplateGalleryDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  onPreview(pkg: DesignPackageSummary): void;
}

export const TemplateGalleryDialog: React.FC<TemplateGalleryDialogProps> = ({ open, onOpenChange, onPreview }) => {
  const [search, setSearch] = React.useState("");
  const q = useDebounced(search, SEARCH_DEBOUNCE_MS).trim();
  const [contextType, setContextType] = React.useState<ContextType | null>(null);
  const [page, setPage] = React.useState(1);

  React.useEffect(() => setPage(1), [q, contextType]);

  const { data, isLoading } = useDesignPackages(
    {
      scope: "global",
      status: "published",
      ...(q ? { q } : {}),
      ...(contextType ? { contextType } : {}),
      page,
      perPage: GALLERY_PAGE_SIZE,
      sort: "publishedAt",
      order: "desc",
    },
    { enabled: open, staleTime: GLOBALS_STALE_TIME }
  );

  const packages = data?.content ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  return (
    <Dialog
      open={open}
      onOpenChange={next => onOpenChange(next)}
      size="3xl"
      showCloseButton
      className="flex flex-col max-h-[85vh]"
    >
      <Dialog.Header>
        <Dialog.Title>Designs from the SocialCoach team</Dialog.Title>
        <Dialog.Description>
          Pick one to preview. Adding it makes an exact copy you can refine and publish.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="flex flex-1 flex-col gap-4 min-h-0 overflow-y-auto">
        <div className="flex items-center gap-3 flex-wrap">
          <InputGroup
            className="max-w-80 w-auto min-w-50"
            control={controlProps => (
              <Input
                {...controlProps}
                value={search}
                placeholder="Search designs…"
                onChange={event => setSearch(event.target.value)}
              />
            )}
            append={<Search />}
          />
          <TypeFilter value={contextType} onChange={setContextType} />
        </div>
        {!isLoading && packages.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-10">No designs match.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {packages.map(pkg => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                variant="global"
                showGlobalBadge={false}
                onClick={() => onPreview(pkg)}
              />
            ))}
          </div>
        )}
        {totalPages > 1 && <PaginationSimple page={page} totalPages={totalPages} onPageChange={setPage} />}
      </Dialog.Content>
    </Dialog>
  );
};
